/**
 * Skup — the parking-lot mess waiting after time away.
 * Hire pays today's idle number. Popping bags pays 5–15% more.
 * The pile is deterministic for a save's lastSeen, so a refresh
 * before cleanup shows the same bags.
 */
import {
  autoPerSec,
  BOTTLE_CLICKER,
  luckyChance,
  offlineWindow,
  spawnPerSec,
  type BottleClickerSave,
} from "./bottleClicker.js";
import {
  DEFAULT_SKUP_PILE_LAYOUTS,
  hoursFromMs,
  layoutForHours,
  type SkupPileLayout,
} from "./skupPileLayout.js";

export type SkupBagTier = "small" | "medium" | "big" | "gold";

export interface SkupBag {
  id: number;
  tier: SkupBagTier;
  grosze: number;
}

export interface SkupPile {
  ms: number;
  /** Coarse preview. Not the exact payout. */
  approximateGrosze: number;
  /** Auto-clear payout — the old flat offline number. */
  hireGrosze: number;
  /** Pop-it-yourself total, 5–15% above hire. */
  manualGrosze: number;
  /** 0 is a short trip, 3 is a capped overnight mess. */
  mess: 0 | 1 | 2 | 3;
  bags: SkupBag[];
}

/** Capped 8 h dump. Enough to bury the lot without stretching each bag. */
export const SKUP_MAX_BAGS = 48;
const MAX_GOLD = 8;

export function bagsForHours(
  hours: number,
  layouts: readonly SkupPileLayout[] = DEFAULT_SKUP_PILE_LAYOUTS,
): number {
  const layout = layoutForHours(layouts, hours);
  if (layout && layout.poses.length > 0) {
    return clamp(layout.poses.length, 1, SKUP_MAX_BAGS);
  }
  const fill = clamp(hours / (BOTTLE_CLICKER.OFFLINE_CAP_MS / (60 * 60 * 1000)), 0, 1);
  return clamp(Math.round(3 + (SKUP_MAX_BAGS - 3) * fill * fill), 3, SKUP_MAX_BAGS);
}

/** Visual-only heap for the admin pile editor. Pays nothing if dismissed. */
export function previewSkupPile(hours: number, bagCount?: number): SkupPile {
  const ms = Math.round(clamp(hours, 1, 8) * 60 * 60 * 1000);
  const count = clamp(bagCount ?? bagsForHours(hours), 1, SKUP_MAX_BAGS);
  const bags: SkupBag[] = [];
  for (let i = 0; i < count; i += 1) {
    const gold = i % 9 === 0;
    bags.push({
      id: i + 1,
      tier: gold ? "gold" : i % 5 === 0 ? "big" : i % 3 === 0 ? "small" : "medium",
      grosze: 0,
    });
  }
  return {
    ms,
    approximateGrosze: 0,
    hireGrosze: 0,
    manualGrosze: 0,
    mess: messLevel(ms),
    bags,
  };
}

export function isPreviewSkupPile(pile: SkupPile | null | undefined): boolean {
  return Boolean(pile && pile.hireGrosze <= 0 && pile.manualGrosze <= 0);
}

export function buildSkupPile(
  save: BottleClickerSave,
  now = Date.now(),
  layouts: readonly SkupPileLayout[] = DEFAULT_SKUP_PILE_LAYOUTS,
): SkupPile | null {
  const { earned, ms } = offlineWindow(save, now);
  if (earned <= 0) return null;

  const rng = mulberry32((save.lastSeenMs ^ Math.imul(earned, 0x9e3779b1)) >>> 0);
  const bonus = 1.05 + rng() * 0.1;
  const hireGrosze = earned;
  const manualGrosze = Math.max(hireGrosze + 1, Math.floor(hireGrosze * bonus));
  const mess = messLevel(ms);
  const bags = makeBags(save, ms, manualGrosze, rng, layouts);

  return {
    ms,
    approximateGrosze: approximateGrosze(manualGrosze),
    hireGrosze,
    manualGrosze,
    mess,
    bags,
  };
}

function messLevel(ms: number): 0 | 1 | 2 | 3 {
  const hour = 60 * 60 * 1000;
  if (ms >= 4 * hour) return 3;
  if (ms >= hour) return 2;
  if (ms >= 10 * 60 * 1000) return 1;
  return 0;
}

function approximateGrosze(grosze: number): number {
  const abs = Math.max(0, Math.floor(grosze));
  const step =
    abs < 500 ? 50 : abs < 5_000 ? 100 : abs < 50_000 ? 500 : abs < 500_000 ? 5_000 : 50_000;
  return Math.max(step, Math.round(abs / step) * step);
}

function makeBags(
  save: BottleClickerSave,
  ms: number,
  manualGrosze: number,
  rng: () => number,
  layouts: readonly SkupPileLayout[],
): SkupBag[] {
  const authored = layoutForHours(layouts, hoursFromMs(ms));
  const fromMoney = clamp(Math.round(4 + Math.log2(1 + manualGrosze / 100)), 3, SKUP_MAX_BAGS);
  const fill = Math.min(1, Math.max(0, ms / BOTTLE_CLICKER.OFFLINE_CAP_MS));
  // The money curve barely moves between 1 h and 8 h. Square the fill so only
  // a capped absence climbs the rest of the way to a full heap.
  const fromTime = clamp(
    Math.round(fromMoney + (SKUP_MAX_BAGS - fromMoney) * fill * fill),
    3,
    SKUP_MAX_BAGS,
  );
  const count =
    authored && authored.poses.length > 0
      ? clamp(authored.poses.length, 1, SKUP_MAX_BAGS)
      : fromTime;

  const seconds = ms / 1000;
  const caught =
    Math.min(spawnPerSec(save.upgrades.spawn), autoPerSec(save.upgrades.auto)) * seconds;
  const gold = clamp(Math.round(caught * luckyChance(save.upgrades.mix)), 0, Math.min(MAX_GOLD, count));

  const weights: number[] = [];
  for (let i = 0; i < count; i += 1) {
    const varied = 0.55 + rng();
    const jackpot = i < gold ? 1.5 + rng() * 2.2 : 1;
    weights.push(varied * jackpot);
  }

  const amounts = splitGrosze(manualGrosze, weights);
  const bags: SkupBag[] = amounts.map((grosze, index) => ({
    id: index + 1,
    tier: index < gold ? "gold" : tierFor(grosze, manualGrosze, count),
    grosze,
  }));
  return shuffle(bags, rng).map((bag, index) => ({ ...bag, id: index + 1 }));
}

function tierFor(grosze: number, total: number, count: number): SkupBagTier {
  const fair = total / count;
  if (grosze >= fair * 1.65) return "big";
  if (grosze >= fair * 0.85) return "medium";
  return "small";
}

function splitGrosze(total: number, weights: number[]): number[] {
  const count = weights.length;
  const sum = weights.reduce((acc, weight) => acc + weight, 0) || 1;
  let remaining = total;
  const amounts: number[] = [];
  for (let i = 0; i < count; i += 1) {
    if (i === count - 1) {
      amounts.push(remaining);
      break;
    }
    const slotsLeft = count - i;
    const fair = remaining / slotsLeft;
    const relative = (weights[i] ?? 1) / (sum / count);
    let share = Math.round(fair * (0.45 + 0.55 * relative));
    share = Math.max(1, Math.min(remaining - (slotsLeft - 1), share));
    amounts.push(share);
    remaining -= share;
  }
  return amounts;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function shuffle<T>(items: T[], rng: () => number): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1));
    const swap = copy[i];
    copy[i] = copy[j] as T;
    copy[j] = swap as T;
  }
  return copy;
}

function mulberry32(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
